/**
 * GameModule das die Solarenergie pro Planet berechnet.
 * Hört auf sun:lifecycle-update und emittiert solar:energy-update.
 * Throttle: maximal 1× pro Sekunde.
 */

import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { SolarEnergyConfig } from '../config/SolarEnergyConfig';
import type { SunLifecycleEntry } from './SunLifecycleTypes';
import { SunState } from './SunLifecycleTypes';
import type { SolarEnergyEntry } from './SolarEnergyTypes';
import { calcSolarEnergy, distanceFactor, ageFactor } from './SolarEnergyFormulas';

/** Internes Record pro Planet (pre-allocated). */
interface PlanetRecord {
  readonly planetId: string;
  readonly starSystemId: string;
  readonly orbitalRing: number;
  readonly luminosity: number;
}

export class SolarEnergySystem implements GameModule {
  readonly name = 'SolarEnergySystem';

  private _config: SolarEnergyConfig;
  private _planets: PlanetRecord[];
  private _entries = new Map<string, SolarEnergyEntry>();
  private _lifecycle: ReadonlyMap<string, SunLifecycleEntry> | null = null;
  private _engine: Engine | null = null;
  private _dirty = true;
  private _timer = 0;

  constructor(
    config: SolarEnergyConfig,
    planets: ReadonlyArray<{ id: string; starSystemId: string; orbitalRing: number; sunLuminosity: number }>,
  ) {
    this._config = config;
    this._planets = planets.map((p) => ({
      planetId: p.id,
      starSystemId: p.starSystemId,
      orbitalRing: p.orbitalRing,
      luminosity: p.sunLuminosity,
    }));
  }

  init(engine: Engine): void {
    this._engine = engine;
    engine.events.on<ReadonlyMap<string, SunLifecycleEntry>>('sun:lifecycle-update', (data) => {
      this._lifecycle = data;
      this._dirty = true;
    });
  }

  update(dt: number): void {
    this._timer += dt;
    if (!this._dirty || this._timer < 1) return;
    this._timer = 0;
    this._dirty = false;
    this._recalculate();
    this._engine?.events.emit('solar:energy-update', this._entries);
  }

  dispose(): void {
    this._entries.clear();
    this._planets.length = 0;
  }

  private _recalculate(): void {
    const cfg = this._config;
    const lifecycle = this._lifecycle;

    for (let i = 0; i < this._planets.length; i++) {
      const p = this._planets[i];
      let sunAge = 0;
      let sunAlive = false;

      if (lifecycle) {
        const sun = lifecycle.get(p.starSystemId);
        if (sun) {
          sunAlive = sun.state === SunState.Alive;
          sunAge = sun.age;
        }
      }

      const energy = calcSolarEnergy(p.orbitalRing, sunAge, p.luminosity, sunAlive, cfg);
      const df = distanceFactor(p.orbitalRing, cfg);
      const af = sunAlive ? ageFactor(sunAge, cfg) : 0;

      this._entries.set(p.planetId, {
        planetId: p.planetId,
        starSystemId: p.starSystemId,
        solarEnergy: Math.round(energy * 10) / 10,
        distanceFactor: df,
        ageFactor: af,
      });
    }
  }
}
